import {FC, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {Controller, useForm} from 'react-hook-form'
import AddressAutocomplete from '../../../../common/component/AddressAutocomplete'
import {MobileNumberInput} from '../../../../common/component/MobileNumberInput'
import {submitToProperty, SubmitToPropertyResult, WizardApiError} from '../../../api'
import {IApplicationFormData} from '../../../models/application-form'
import ApplicationResponsiveModal from '../../common/ApplicationResponsiveModal'
import {useSubmissionValidationReporting} from './useSubmissionValidationReporting'
import {
  AU_MOBILE_REGEX,
  formatAustralianMobileInput,
  isValidAustralianAgentPhone,
  normalizeMobile,
  type SubmitToPropertyPrefill,
} from '../renterHomeUtils'

// ─── Types ────────────────────────────────────────────────────────────────────

type PropertyDetailsForm = {
  propertyAddress: string
  placeId?: string
  agentName: string
  agentPhone: string
  agentEmail?: string
}

type AdditionalAgent = {
  id: number
  name: string
  phone: string
  email?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toAnnual = (amount?: number, frequency?: string): number => {
  if (!amount) return 0
  const multipliers: Record<string, number> = {weekly: 52, fortnightly: 26, monthly: 12, annual: 1}
  return amount * (multipliers[frequency ?? ''] ?? 1)
}

const titleCase = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const normalizeAgentEmail = (value?: string): string => (value ?? '').trim().toLowerCase()

const formatOrdinal = (value: number) => {
  const remainder = value % 100
  if (remainder >= 11 && remainder <= 13) return `${value}th`

  switch (value % 10) {
    case 1:
      return `${value}st`
    case 2:
      return `${value}nd`
    case 3:
      return `${value}rd`
    default:
      return `${value}th`
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const RequiredBadge: FC = () => (
  <span
    style={{
      backgroundColor: '#fef2f2',
      color: '#ef4444',
      border: '1px solid #fecaca',
      borderRadius: '4px',
      padding: '1px 6px',
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    }}
  >
    Required
  </span>
)

const SendIcon: FC<{color?: string}> = ({color = 'currentColor'}) => (
  <i className='bi bi-send-fill' style={{flexShrink: 0, fontSize: '17px', color}} />
)

const CheckIcon: FC = () => (
  <i className='bi bi-check-lg' style={{flexShrink: 0, fontSize: '14px', color: '#22c55e'}} />
)

const HomeIcon: FC = () => (
  <i className='bi bi-house' style={{flexShrink: 0, fontSize: '20px', color: '#374151'}} />
)

// ─── Success Screen ───────────────────────────────────────────────────────────

type SuccessScreenProps = {
  result: SubmitToPropertyResult
  onClose: () => void
}

const SuccessScreen: FC<SuccessScreenProps> = ({result, onClose}) => (
  <div className='text-center py-6 px-4'>
    <div
      className='d-inline-flex align-items-center justify-content-center rounded-circle mb-5'
      style={{width: 64, height: 64, backgroundColor: '#f0fdf4'}}
    >
      <i className='bi bi-check-lg' style={{fontSize: '28px', color: '#22c55e'}} />
    </div>

    <h4 className='fw-bold mb-2'>Doora Card Sent!</h4>

    {result.sentVia === 'email' ? (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        Your application has been emailed to the property manager.
      </p>
    ) : result.sentVia === 'multiple' ? (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        Your application has been sent to the agents using their available contact details.
      </p>
    ) : result.sentVia === 'admin_email' ? (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        The phone number entered cannot receive SMS. Doora admin has been notified to collect the
        agent email.
      </p>
    ) : (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        The agent has been sent an SMS with a link to receive your application.
      </p>
    )}

    {result.deliveryWarning && (
      <div
        className='rounded-3 p-3 mb-4 text-start'
        style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          fontSize: '0.85rem',
          color: '#92400e',
        }}
      >
        <strong>Heads up:</strong> {result.deliveryWarning}
      </div>
    )}

    <button type='button' className='btn btn-doora w-100 rounded-3 py-3 fw-bold' onClick={onClose}>
      Done
    </button>
  </div>
)

// ─── Error Screen ─────────────────────────────────────────────────────────────

type ErrorScreenProps = {
  result: SubmitToPropertyResult
  onClose: () => void
  actionLabel: string
}

const ErrorScreen: FC<ErrorScreenProps> = ({result, onClose, actionLabel}) => (
  <div className='text-center py-6 px-4'>
    <div
      className='d-inline-flex align-items-center justify-content-center rounded-circle mb-5'
      style={{width: 64, height: 64, backgroundColor: '#fef2f2'}}
    >
      <i className='bi bi-x-lg' style={{fontSize: '28px', color: '#ef4444'}} />
    </div>

    <h4 className='fw-bold mb-2'>Delivery Failed</h4>

    {result.sentVia === 'email' ? (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        We couldn't deliver your Doora Card to the agent's email address.
      </p>
    ) : result.sentVia === 'multiple' ? (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        We couldn't notify one or more agents.
      </p>
    ) : result.sentVia === 'sms' ? (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        We couldn't send the SMS notification to the agent.
      </p>
    ) : (
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        We were unable to deliver your Doora Card to the agent.
      </p>
    )}

    {result.deliveryWarning && (
      <div
        className='rounded-3 p-3 mb-4 text-start'
        style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          fontSize: '0.85rem',
          color: '#92400e',
        }}
      >
        <strong>Note:</strong> {result.deliveryWarning}
      </div>
    )}

    <button type='button' className='btn btn-doora w-100 rounded-3 py-3 fw-bold' onClick={onClose}>
      {actionLabel}
    </button>
  </div>
)

type BlockedSubmissionScreenProps = {
  result: SubmitToPropertyResult
  onClose: () => void
  actionLabel: string
}

const getBlockReasonName = (
  reason: NonNullable<SubmitToPropertyResult['blockReasons']>[number]
): string => reason.fullName?.trim() || (reason.role ? titleCase(reason.role) : 'Household member')

const getBlockReasonDetails = (
  reason: NonNullable<SubmitToPropertyResult['blockReasons']>[number]
): string[] => {
  const messages = (reason.reasonMessages ?? []).filter(Boolean)
  if (messages.length > 0) {
    return messages
  }

  return (reason.reasonCodes ?? []).filter(Boolean).map(titleCase)
}

const BlockedSubmissionScreen: FC<BlockedSubmissionScreenProps> = ({
  result,
  onClose,
  actionLabel,
}) => {
  const reasons = result.blockReasons ?? []

  return (
    <div className='text-center py-6 px-4'>
      <div
        className='d-inline-flex align-items-center justify-content-center rounded-circle mb-5'
        style={{width: 64, height: 64, backgroundColor: '#fffbeb'}}
      >
        <i className='bi bi-exclamation-triangle' style={{fontSize: '28px', color: '#b45309'}} />
      </div>

      <h4 className='fw-bold mb-2'>Household needs attention</h4>
      <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
        {result.message || 'This household cannot be submitted yet.'}
      </p>

      {reasons.length > 0 && (
        <div className='text-start mb-4' style={{display: 'grid', gap: 10}}>
          {reasons.map((reason, index) => {
            const details = getBlockReasonDetails(reason)

            return (
              <div
                key={reason.householdMemberId ?? reason.householdInviteId ?? index}
                className='rounded-3 p-3'
                style={{
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  color: '#92400e',
                  fontSize: '0.85rem',
                }}
              >
                <strong>{getBlockReasonName(reason)}</strong>
                <div>
                  {details.length > 0 ? details.join(' ') : 'This member is not ready yet.'}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <button
        type='button'
        className='btn btn-doora w-100 rounded-3 py-3 fw-bold'
        onClick={onClose}
      >
        {actionLabel}
      </button>
    </div>
  )
}

type RequestFailureScreenProps = {
  onClose: () => void
  actionLabel: string
}

const RequestFailureScreen: FC<RequestFailureScreenProps> = ({onClose, actionLabel}) => (
  <div className='text-center py-6 px-4'>
    <div
      className='d-inline-flex align-items-center justify-content-center rounded-circle mb-5'
      style={{width: 64, height: 64, backgroundColor: '#fef2f2'}}
    >
      <i className='bi bi-exclamation-triangle' style={{fontSize: '28px', color: '#ef4444'}} />
    </div>

    <h4 className='fw-bold mb-2'>We couldn't send your Doora Card</h4>

    <p className='text-muted mb-4' style={{fontSize: '0.9rem'}}>
      Something went wrong while submitting your Doora Card. We couldn't confirm whether your
      submission was successful. Please check your Submissions before trying again.
    </p>

    <button type='button' className='btn btn-doora w-100 rounded-3 py-3 fw-bold' onClick={onClose}>
      {actionLabel}
    </button>
  </div>
)

// ─── Add Agent Modal ──────────────────────────────────────────────────────────

type AddAgentModalProps = {
  onClose: () => void
  onSave: (agent: Omit<AdditionalAgent, 'id'>) => void
  existingEmails: string[]
  existingPhones: string[]
}

const AddAgentModal: FC<AddAgentModalProps> = ({
  onClose,
  onSave,
  existingEmails,
  existingPhones,
}) => {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<{name?: string; phone?: string; email?: string}>({})

  const handleSave = () => {
    const trimmedName = name.trim()
    const normalizedPhone = normalizeMobile(phone)
    const trimmedEmail = email.trim()
    const normalizedEmail = normalizeAgentEmail(trimmedEmail)
    const nextErrors: {name?: string; phone?: string; email?: string} = {}

    if (!trimmedName) {
      nextErrors.name = 'Agent name is required'
    }

    if (!AU_MOBILE_REGEX.test(normalizedPhone)) {
      nextErrors.phone = 'Please enter a valid phone number'
    } else if (existingPhones.includes(normalizedPhone)) {
      nextErrors.phone = 'This phone number has already been added'
    }

    if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) {
      nextErrors.email = 'Please enter a valid email address'
    } else if (normalizedEmail && existingEmails.includes(normalizedEmail)) {
      nextErrors.email = 'This email has already been added'
    }

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      return
    }

    onSave({name: trimmedName, phone: normalizedPhone, email: trimmedEmail || undefined})
    onClose()
  }

  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='add-agent-modal-title'
      closeAriaLabel='Close add agent dialog'
      onClose={onClose}
      modalClassName='application-household-modal'
      closeButtonClassName='application-household-modal-close'
      mobileAnimationMs={380}
      swipeTarget='sheet'
    >
      {(closeModal) => (
        <div className='application-household-modal-body'>
          <h2 id='add-agent-modal-title' className='application-household-modal-title mb-3'>
            Add agent
          </h2>

          <div className='mt-4 mb-3'>
            <label className='form-label fw-semibold' htmlFor='additional-agent-name'>
              Agent Name
            </label>
            <input
              id='additional-agent-name'
              type='text'
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                setErrors((current) => ({...current, name: undefined}))
              }}
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              placeholder='Enter agent name'
              autoFocus
            />
            {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
          </div>

          <div className='mb-3'>
            <label className='form-label fw-semibold' htmlFor='additional-agent-phone'>
              Agent Phone
            </label>
            <input
              id='additional-agent-phone'
              type='tel'
              value={formatAustralianMobileInput(phone)}
              onChange={(event) => {
                setPhone(normalizeMobile(event.target.value).slice(0, 10))
                setErrors((current) => ({...current, phone: undefined}))
              }}
              className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
              placeholder='0412 345 678'
            />
            {errors.phone && <div className='invalid-feedback'>{errors.phone}</div>}
          </div>

          <div className='mb-3'>
            <label className='form-label fw-semibold' htmlFor='additional-agent-email'>
              Agent Email (optional)
            </label>
            <input
              id='additional-agent-email'
              type='email'
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setErrors((current) => ({...current, email: undefined}))
              }}
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              placeholder='agent@example.com.au'
            />
            {errors.email && <div className='invalid-feedback'>{errors.email}</div>}
          </div>

          <div className='d-flex gap-2 mt-4'>
            <button
              type='button'
              className='btn btn-light'
              style={{minWidth: 150}}
              onClick={closeModal}
            >
              Cancel
            </button>
            <button type='button' className='btn btn-doora flex-fill' onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      )}
    </ApplicationResponsiveModal>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

type SubmitToPropertyContentProps = {
  sessionId: string
  draft: IApplicationFormData
  onClose: () => void
  onSubmitted?: () => void
  failureActionLabel?: string
  initialDetails?: SubmitToPropertyPrefill
}

export const SubmitToPropertyContent: FC<SubmitToPropertyContentProps> = ({
  sessionId,
  draft,
  onClose,
  onSubmitted,
  failureActionLabel = 'Close',
  initialDetails,
}) => {
  const initialPrimaryAgent = initialDetails?.agents?.[0]
  const initialFormValues = useMemo<PropertyDetailsForm>(
    () => ({
      propertyAddress: initialDetails?.propertyAddress ?? '',
      placeId: initialDetails?.propertyPlaceId,
      agentName: initialPrimaryAgent?.agentName ?? initialDetails?.agentName ?? '',
      agentPhone: initialPrimaryAgent?.agentPhone
        ? formatAustralianMobileInput(initialPrimaryAgent.agentPhone)
        : initialDetails?.agentPhone
        ? formatAustralianMobileInput(initialDetails.agentPhone)
        : '',
      agentEmail: initialPrimaryAgent?.agentEmail ?? initialDetails?.agentEmail ?? '',
    }),
    [
      initialDetails?.agentEmail,
      initialDetails?.agentName,
      initialDetails?.agentPhone,
      initialDetails?.agents,
      initialDetails?.propertyAddress,
      initialDetails?.propertyPlaceId,
      initialPrimaryAgent?.agentEmail,
      initialPrimaryAgent?.agentName,
      initialPrimaryAgent?.agentPhone,
    ]
  )
  const initialAdditionalAgents = useMemo<AdditionalAgent[]>(
    () =>
      (initialDetails?.agents ?? []).slice(1).map((agent, index) => ({
        id: index + 1,
        name: agent.agentName,
        phone: normalizeMobile(agent.agentPhone),
        email: agent.agentEmail,
      })),
    [initialDetails?.agents]
  )
  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
    reset,
    setValue,
    clearErrors,
    getValues,
  } = useForm<PropertyDetailsForm>({defaultValues: initialFormValues})
  const {onInvalidSubmit, onNativeInvalid} =
    useSubmissionValidationReporting<PropertyDetailsForm>(sessionId)

  const [addressResetKey, setAddressResetKey] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitResult, setSubmitResult] = useState<SubmitToPropertyResult | null>(null)
  const [hasSubmitRequestFailed, setHasSubmitRequestFailed] = useState(false)
  const [isAddAgentModalOpen, setIsAddAgentModalOpen] = useState(false)
  const [additionalAgents, setAdditionalAgents] =
    useState<AdditionalAgent[]>(initialAdditionalAgents)

  // ── Derived card display values ──────────────────────────────────────────────

  const applicantName = [draft.firstName, draft.lastName].filter(Boolean).join(' ')

  const applicantTypeLabel = useMemo(() => {
    const type = draft.applicationType ?? ''
    if (type.toLowerCase() === 'individual') return 'Individual · 1 applicant'
    const count =
      draft.reviewSummary?.activeMembers?.length ?? (draft.coApplicants?.length ?? 0) + 1
    return `${titleCase(type)} · ${count} applicants`
  }, [draft.applicationType, draft.coApplicants, draft.reviewSummary?.activeMembers])

  const incomeDisplay = useMemo(() => {
    const income = draft.income
    const total =
      toAnnual(income?.income, income?.incomeFrequency) +
      toAnnual(income?.selfEmploymentIncome, income?.selfEmploymentIncomeFrequency) +
      toAnnual(income?.centreAmount, income?.centreLinkFrequency) +
      toAnnual(income?.pensionAmount, income?.pensionFrequency) +
      toAnnual(income?.ndisAmount, income?.ndisFrequency) +
      toAnnual(income?.studentSupportAmount, income?.studentSupportFrequency)
    return total > 0 ? `$${Math.round(total).toLocaleString('en-AU')}/yr` : 'Not specified'
  }, [draft.income])

  const employmentDisplay = useMemo(() => {
    const type = draft.employment?.employmentType?.trim()
    return type ? titleCase(type) : 'Not specified'
  }, [draft.employment])

  const rentalHistoryDisplay = useMemo(() => {
    if (draft.previousRentalHistory && draft.previousRentDuration) return draft.previousRentDuration
    return 'None'
  }, [draft.previousRentalHistory, draft.previousRentDuration])

  const preferenceDisplay = useMemo(() => {
    const leaseLabels: Record<string, string> = {
      '6-months': '6 mo',
      '12-months': '12 mo',
      '24-months': '24 mo',
    }
    const lease = leaseLabels[draft.preferredLeaseTerm ?? ''] ?? draft.preferredLeaseTerm ?? ''
    const min = draft.rentWeeklyBudgetMin
    const max = draft.rentWeeklyBudgetMax
    if (lease && min != null && max != null)
      return `${lease} · $${Math.round(min).toLocaleString('en-AU')}–$${Math.round(
        max
      ).toLocaleString('en-AU')}/wk`
    if (lease) return lease
    return 'Not specified'
  }, [draft.preferredLeaseTerm, draft.rentWeeklyBudgetMin, draft.rentWeeklyBudgetMax])

  const retentionDisplay = useMemo(() => {
    // RetentionType enum: 1=KeepUntilIDelete, 2=AutoDelete90Days, 3=AutoDelete60Days
    if (draft.retentionPreference === 2) return 'Auto-deletes in 90 days'
    if (draft.retentionPreference === 3) return 'Auto-deletes in 60 days'
    return 'No auto-expiry'
  }, [draft.retentionPreference])

  const householdEligibility = draft.reviewSummary?.householdEligibility
  const householdWarnings = householdEligibility?.warnings ?? []
  const householdBlockerMessages = useMemo(
    () =>
      Array.from(
        new Set(
          (householdEligibility?.blockingReasons ?? [])
            .map((blocker) => blocker.message.trim())
            .filter(Boolean)
        )
      ),
    [householdEligibility]
  )
  const isHouseholdReady = householdEligibility?.canSubmit === true

  const cardDocuments = useMemo(() => {
    const docs = (draft.documents ?? []).filter((d) => d.filename)
    const result: Array<{label: string; detail: string}> = []

    const hasFront = docs.some((d) => d.type === 'identity-front')
    const hasBack = docs.some((d) => d.type === 'identity-back')
    if (hasFront || hasBack)
      result.push({
        label: 'Driver Licence',
        detail: hasFront && hasBack ? 'Front + Back' : hasFront ? 'Front only' : 'Back only',
      })

    const payslips = docs.filter((d) =>
      ['income-doc-1', 'income-doc-2', 'income-doc-3'].includes(d.type ?? '')
    ).length
    if (payslips > 0) result.push({label: 'Payslips', detail: `${payslips} uploaded`})

    const ledgers = docs.filter((d) => d.type === 'rental-ledger').length
    if (ledgers > 0) result.push({label: 'Rental Ledger', detail: `${ledgers} uploaded`})

    const visas = docs.filter((d) => d.type === 'visa-grant-notice').length
    if (visas > 0) result.push({label: 'Visa Grant Notice', detail: `${visas} uploaded`})

    const coes = docs.filter((d) => d.type === 'international-coe').length
    if (coes > 0) result.push({label: 'Certificate of Enrolment', detail: `${coes} uploaded`})

    const enrollments = docs.filter((d) => d.type === 'enrollment-letter').length
    if (enrollments > 0) result.push({label: 'Enrolment Letter', detail: `${enrollments} uploaded`})

    const loas = docs.filter((d) => d.type === 'letter-of-authority').length
    if (loas > 0) result.push({label: 'Letter of Authority', detail: `${loas} uploaded`})

    if (draft.emergencyContactName) result.push({label: 'Emergency Contact', detail: 'On file'})

    return result
  }, [draft.documents, draft.emergencyContactName])

  const cardInfo = [
    {label: 'Income', value: incomeDisplay},
    {label: 'Employment', value: employmentDisplay},
    {label: 'Rental History', value: rentalHistoryDisplay},
    {label: 'Preference', value: preferenceDisplay},
  ]

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleClose = () => {
    reset(initialFormValues)
    setAddressResetKey((k) => k + 1)
    setSubmitError(null)
    setSubmitResult(null)
    setHasSubmitRequestFailed(false)
    setIsAddAgentModalOpen(false)
    setAdditionalAgents(initialAdditionalAgents)
    onClose()
  }

  const handleAddAgent = (agent: Omit<AdditionalAgent, 'id'>) => {
    setAdditionalAgents((current) => {
      const nextId = current.reduce((highestId, item) => Math.max(highestId, item.id), 0) + 1
      return [...current, {...agent, id: nextId}]
    })
  }

  const onSubmit = async (data: PropertyDetailsForm) => {
    if (!sessionId) {
      setSubmitError('Session expired. Please verify your mobile and try again.')
      return
    }

    const propertyPlaceId = data.placeId?.trim() || undefined

    setIsSubmitting(true)
    setSubmitError(null)
    setHasSubmitRequestFailed(false)

    try {
      const normalizedPrimaryPhone = normalizeMobile(data.agentPhone)
      const agents = [
        {
          agentName: data.agentName,
          agentPhone: normalizedPrimaryPhone,
          agentEmail: data.agentEmail?.trim() || undefined,
        },
        ...additionalAgents.map((agent) => ({
          agentName: agent.name,
          agentPhone: agent.phone,
          agentEmail: agent.email,
        })),
      ]

      const result = await submitToProperty({
        sessionId,
        propertyAddress: data.propertyAddress,
        propertyPlaceId,
        agentName: data.agentName,
        agentPhone: normalizedPrimaryPhone,
        agentEmail: data.agentEmail?.trim() || undefined,
        agents,
      })

      setSubmitResult(result)
      onSubmitted?.()
    } catch (error) {
      if (error instanceof WizardApiError && error.isBlocked) {
        setSubmitResult({
          isBlocked: true,
          blockCode: error.blockCode,
          message: error.message,
          blockReasons: error.blockReasons,
          submissionId: '',
          token: '',
          secureViewUrl: '',
          status: 'blocked',
          sentVia: 'none',
          deliverySucceeded: false,
        })
        setSubmitError(null)
        setHasSubmitRequestFailed(false)
        return
      }

      setHasSubmitRequestFailed(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const addAgentExistingPhones = isAddAgentModalOpen
    ? [
        normalizeMobile(getValues('agentPhone') ?? ''),
        ...additionalAgents.map((agent) => agent.phone),
      ]
        .map((value) => value.trim())
        .filter(Boolean)
    : []
  const addAgentExistingEmails = isAddAgentModalOpen
    ? [
        normalizeAgentEmail(getValues('agentEmail')),
        ...additionalAgents.map((agent) => normalizeAgentEmail(agent.email)),
      ].filter(Boolean)
    : []

  return (
    <>
      {submitResult?.isBlocked ? (
        <BlockedSubmissionScreen
          result={submitResult}
          onClose={handleClose}
          actionLabel={failureActionLabel}
        />
      ) : submitResult && submitResult.deliverySucceeded ? (
        <SuccessScreen result={submitResult} onClose={handleClose} />
      ) : submitResult && !submitResult.deliverySucceeded ? (
        <ErrorScreen result={submitResult} onClose={handleClose} actionLabel={failureActionLabel} />
      ) : hasSubmitRequestFailed ? (
        <RequestFailureScreen onClose={handleClose} actionLabel={failureActionLabel} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} onInvalidCapture={onNativeInvalid}>
          <div className='submit-to-property'>
            <div className='step-body'>
              <h1 className='step-title'>Send your application</h1>
              {/* ── Property Details ─────────────────────────────────────── */}
              <div className='card'>
                <div className='card-head'>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 20 20'
                    fill='none'
                    stroke='#111828'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M3 10l7-6 7 6v7a1 1 0 01-1 1h-4v-5H8v5H4a1 1 0 01-1-1v-7z'></path>
                  </svg>
                  <div className='card-title'>Property Details</div>
                </div>
                <div className='stack'>
                  <div>
                    <div className='input-label'>
                      <label className='field-label'>Property Address</label>
                      <span className='pill pill-req'>Required</span>
                    </div>
                    <Controller
                      name='propertyAddress'
                      control={control}
                      rules={{
                        required: 'Property address is required',
                        validate: (value) => Boolean(value.trim()) || 'Property address is required',
                        maxLength: {
                          value: 500,
                          message: 'Property address must not exceed 500 characters',
                        },
                      }}
                      render={({field: {onChange}}) => (
                        <AddressAutocomplete
                          label=''
                          key={addressResetKey}
                          initialValue={initialFormValues.propertyAddress}
                          onInputChange={(value) => {
                            onChange(value)
                            setValue('placeId', undefined)
                            clearErrors('propertyAddress')
                          }}
                          onSelect={(id, address) => {
                            onChange(address)
                            setValue('placeId', id)
                            clearErrors('propertyAddress')
                          }}
                          inputClassName={errors.propertyAddress ? 'is-invalid' : ''}
                          wrapperClassName=''
                          wizardSessionId={sessionId}
                        />
                      )}
                    />
                    {errors.propertyAddress && (
                      <div className='invalid-feedback d-block'>
                        {errors.propertyAddress.message}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className='input-label'>
                      <label className='field-label'>Agent Name</label>
                      <span className='pill pill-req'>Required</span>
                    </div>
                    <input
                      type='text'
                      className={`form-control form-control-lg ${
                        errors.agentName ? 'is-invalid' : ''
                      }`}
                      {...register('agentName', {required: 'Agent name is required'})}
                    />
                    {errors.agentName && (
                      <div className='invalid-feedback'>{errors.agentName.message}</div>
                    )}
                  </div>
                  <div>
                    <div className='input-label'>
                      <label className='field-label'>Agent Phone</label>
                      <span className='pill pill-req'>Required</span>
                    </div>
                    <MobileNumberInput<PropertyDetailsForm>
                      name='agentPhone'
                      control={control}
                      errors={errors}
                      className='mb-0'
                      inputClassName='form-control-lg'
                      rules={{
                        required: 'Agent phone is required',
                        validate: (value) => {
                          const normalizedPhone = normalizeMobile(String(value))
                          if (!isValidAustralianAgentPhone(normalizedPhone)) {
                            return 'Please enter a valid Australian mobile or landline number'
                          }

                          return (
                            !additionalAgents.some((agent) => agent.phone === normalizedPhone) ||
                            'This phone number has already been added'
                          )
                        },
                      }}
                    />
                  </div>
                  <div>
                    <div className='input-label'>
                      <label className='field-label'>Agent Email (optional)</label>
                    </div>
                    <input
                      type='email'
                      className={`form-control form-control-lg ${
                        errors.agentEmail ? 'is-invalid' : ''
                      }`}
                      {...register('agentEmail', {
                        pattern: {
                          value: EMAIL_REGEX,
                          message: 'Please enter a valid email address',
                        },
                        validate: (value) => {
                          const normalizedEmail = normalizeAgentEmail(value)
                          if (!normalizedEmail) {
                            return true
                          }

                          return (
                            !additionalAgents.some(
                              (agent) => normalizeAgentEmail(agent.email) === normalizedEmail
                            ) || 'This email has already been added'
                          )
                        },
                      })}
                    />
                    {errors.agentEmail && (
                      <div className='invalid-feedback'>{errors.agentEmail.message}</div>
                    )}
                    <p className='text-muted mt-2 mb-0' style={{fontSize: '0.8rem'}}>
                      If no email is provided, the agent will receive an SMS with a link to enter
                      it.
                    </p>
                  </div>
                  {additionalAgents.map((agent, index) => (
                    <div className='border rounded-3 p-3' key={agent.id}>
                      <div className='d-flex align-items-center justify-content-between mb-3'>
                        <strong>Agent {index + 2}</strong>
                        <button
                          type='button'
                          className='remove'
                          onClick={() =>
                            setAdditionalAgents((current) =>
                              current.filter((currentAgent) => currentAgent.id !== agent.id)
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                      <div className='stack' style={{gap: '10px'}}>
                        <div className='agent-name'>{agent.name}</div>
                        <div className='agent-number'>
                          {formatAustralianMobileInput(agent.phone)}
                        </div>
                        {agent.email && <div className='agent-number'>{agent.email}</div>}
                      </div>
                    </div>
                  ))}
                  <button
                    type='button'
                    className='add-agent'
                    onClick={() => setIsAddAgentModalOpen(true)}
                  >
                    Add {formatOrdinal(additionalAgents.length + 2)} agent
                  </button>
                </div>
              </div>
              {householdEligibility?.canSubmit === false && (
                <div className='info'>
                  <div className='icon'>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#B45309'
                      strokeWidth='2.2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <circle cx='10' cy='10' r='7'></circle>
                      <path d='M10 6v4l2.5 2'></path>
                    </svg>
                  </div>
                  <div>
                    <strong>Household needs attention.</strong>
                    {householdBlockerMessages.length > 0 && (
                      <ul className='mb-0 mt-2'>
                        {householdBlockerMessages.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {/* ── Doora Card Preview ───────────────────────────────────── */}
              <div className='summary'>
                <div className='wrap'>
                  <div className='head'>
                    <span className='title'>DOORA CARD</span>
                    <span className='ready'>
                      <span className='icon'></span>
                      {isHouseholdReady ? 'Ready' : 'Check readiness'}
                    </span>
                  </div>
                  <div className='body'>{applicantName}</div>
                  <div className='footer'>{applicantTypeLabel} </div>
                </div>
              </div>
              {householdWarnings.length > 0 && (
                <div className='info' role='status'>
                  <div className='icon'>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#B45309'
                      strokeWidth='2.2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <circle cx='10' cy='10' r='7'></circle>
                      <path d='M10 6v4l2.5 2'></path>
                    </svg>
                  </div>
                  <div>
                    <strong>These invitees will not be included.</strong>
                    <ul className='mb-0 mt-2'>
                      {householdWarnings.map((warning) => (
                        <li key={`${warning.code}-${warning.householdInviteId}`}>{warning.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Error Message ────────────────────────────────────────── */}
          {submitError && (
            <div
              className='rounded-3 p-3 mb-4'
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                fontSize: '0.875rem',
                color: '#dc2626',
              }}
            >
              {submitError}
            </div>
          )}

          {/* ── Send Button ──────────────────────────────────────────── */}
          <button type='submit' className='send-doora' disabled={isSubmitting}>
            <svg
              width='18'
              height='18'
              viewBox='0 0 20 20'
              fill='none'
              stroke='#fff'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M17 3L2 9l6 2 2 6 7-14z'></path>
              <path d='M8 11l4-4'></path>
            </svg>{' '}
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                />
                Sending...
              </>
            ) : (
              <>Send Your Doora Card</>
            )}
          </button>
        </form>
      )}
      {isAddAgentModalOpen && (
        <AddAgentModal
          existingEmails={addAgentExistingEmails}
          existingPhones={addAgentExistingPhones}
          onClose={() => setIsAddAgentModalOpen(false)}
          onSave={handleAddAgent}
        />
      )}
    </>
  )
}

type Props = {
  show: boolean
  onHide: () => void
  sessionId: string
  draft: IApplicationFormData
  onSubmitted?: () => void
  initialDetails?: SubmitToPropertyPrefill
}

const SubmitToPropertyModal: FC<Props> = ({
  show,
  onHide,
  sessionId,
  draft,
  onSubmitted,
  initialDetails,
}) => (
  <Modal show={show} onHide={onHide} dialogClassName='modal-fullscreen-sm-down' centered scrollable>
    <Modal.Header closeButton className='border-0 pb-0' />
    <Modal.Body className='pt-2 px-3 px-sm-5 pb-6'>
      <SubmitToPropertyContent
        sessionId={sessionId}
        draft={draft}
        onClose={onHide}
        onSubmitted={onSubmitted}
        initialDetails={initialDetails}
      />
    </Modal.Body>
  </Modal>
)

export default SubmitToPropertyModal
